import type { Nutrients } from '../data-model/food';

/**
 * Reading one hit of the food search index, in one place for the app and the server (#5623).
 *
 * Both turn a hit into a food list row and rank it with `rankFoodHeaders`, which reads the id, the
 * name, the search score and, for a staple, the other names it was found under. Each caller adds
 * what only it needs on top: the app the numbers its list shows, the server nothing.
 */

/** the part of a search hit this module reads; a real Elasticsearch hit satisfies it */
export interface FoodSearchHit {
  _id: string;
  _score?: number;
  _source: { name: string; nutrients?: Nutrients; altNames?: unknown };
}

/** What the ranking reads from a hit, plus the stored nutrients for whoever shows them. */
export interface FoodSearchHitFields {
  _id: string;
  name: string;
  /** the search score; its presence is also how the ranking tells a server result from a device food */
  score: number | undefined;
  altNames: string[] | undefined;
  nutrients: Nutrients | undefined;
}

/**
 * The fields of one search hit. A hit with no `_source` throws here, as it always did in the app's
 * search, so the caller's own failure handling reports a damaged answer instead of ranking a food
 * with no name.
 */
export function readFoodSearchHit(hit: FoodSearchHit): FoodSearchHitFields {
  const source = hit._source;
  return {
    _id: hit._id,
    name: source.name,
    score: hit._score,
    altNames: readHitAltNames(source),
    nutrients: source.nutrients,
  };
}

/**
 * The other names a search hit was found under, so the food list can credit a staple found by one of
 * them (food staples plan 3). Only text entries are kept; a damaged value gives none, not a guess.
 */
function readHitAltNames(source: { altNames?: unknown } | undefined): string[] | undefined {
  if (!Array.isArray(source?.altNames)) {
    return undefined;
  }
  const altNames = source.altNames.filter((altName: unknown): altName is string => typeof altName === 'string');
  return altNames.length > 0 ? altNames : undefined;
}
